import axios from "axios";
import { api } from "../config";
import * as url from "../helpers/url_helper";

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Helper function to check if token is expired (defined before use)
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // JWT tokens have 3 parts separated by dots: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    // Decode the payload (second part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check if token has expiration claim (exp)
    if (payload.exp) {
      // exp is in seconds, Date.now() is in milliseconds
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    }
    
    // If no expiration claim, assume token is valid
    return false;
  } catch (error) {
    // If we can't parse the token, consider it expired
    return true;
  }
};

// Helper function to handle logout and redirect
const handleLogout = () => {
  sessionStorage.removeItem("authUser");
  delete axios.defaults.headers.common["Authorization"];
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
};

// In-flight refresh promise so multiple 401s only trigger one refresh
let refreshPromise = null;

const getStoredRefreshToken = () => {
  try {
    const authUser = sessionStorage.getItem("authUser");
    if (!authUser) return null;
    const data = JSON.parse(authUser);
    return data?.refreshToken ?? data?.refresh_token ?? null;
  } catch {
    return null;
  }
};

const doRefreshToken = (refreshToken) => {
  return axios
    .post(url.BACKEND_REFRESH_TOKEN, { refreshToken }, { _isRefreshRequest: true })
    .then((response) => {
      const raw = response?.data ?? response;
      // Support { data: [ { token, refreshToken } ] } or { token, refreshToken }
      const item = raw?.data?.[0] ?? (Array.isArray(raw) ? raw[0] : raw);
      const newToken = item?.token ?? item?.accessToken ?? item?.access_token;
      const newRefreshToken = item?.refreshToken ?? item?.refresh_token ?? refreshToken;
      if (!newToken) throw new Error("Refresh did not return a token");
      try {
        const authUser = sessionStorage.getItem("authUser");
        const authData = authUser ? JSON.parse(authUser) : {};
        authData.token = authData.accessToken = authData.access_token = newToken;
        authData.refreshToken = authData.refresh_token = newRefreshToken;
        sessionStorage.setItem("authUser", JSON.stringify(authData));
      } catch (e) {
        sessionStorage.removeItem("authUser");
        throw e;
      }
      axios.defaults.headers.common["Authorization"] = "Bearer " + newToken;
      return newToken;
    });
};

// Set token from sessionStorage on load. If token is expired we still set it so the first
// request gets 401 and the response interceptor can try refresh (no immediate logout).
const authUser = sessionStorage.getItem("authUser");
if (authUser) {
  try {
    const userData = JSON.parse(authUser);
    const token = userData.token || userData.accessToken || userData.access_token;
    if (token) {
      axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    }
  } catch (e) {
    sessionStorage.removeItem("authUser");
  }
}


// Request interceptor to check token expiration before making requests
axios.interceptors.request.use(
  function (config) {
    // When sending FormData (file upload), omit Content-Type so axios sends multipart/form-data with boundary (payload: file field only, like -F "file=@...").
    if (config.data && typeof FormData !== "undefined" && config.data instanceof FormData) {
      if (config.headers) {
        delete config.headers["Content-Type"];
        delete config.headers["content-type"];
      }
    }
    // Do not logout when token is expired — let the request go; server returns 401 and
    // the response interceptor will try refresh and retry. Logout only when refresh fails.
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response interceptor: success pass-through; on 401 try refresh then retry, else reject (no immediate logout on 401)
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    const status = error.response?.status || error.status;
    const config = error.config;

    if (status === 401 && config && !config._isRefreshRequest) {
      // Already retried once with new token and got 401 again — avoid infinite loop
      if (config._retry) {
        handleLogout();
        const message = error.response?.data?.message || "Session expired. Please login again.";
        return Promise.reject({ message, status, data: error.response?.data });
      }
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) {
        handleLogout();
        const message = error.response?.data?.message || "Session expired. Please login again.";
        return Promise.reject({ message, status, data: error.response?.data });
      }
      const retryWithNewToken = (newToken) => {
        config.headers = { ...config.headers, Authorization: "Bearer " + newToken };
        config._retry = true;
        return axios(config);
      };
      if (refreshPromise) {
        return refreshPromise.then(retryWithNewToken);
      }
      refreshPromise = doRefreshToken(refreshToken)
        .then((newToken) => {
          refreshPromise = null;
          return newToken;
        })
        .catch((err) => {
          refreshPromise = null;
          handleLogout();
          const msg = err?.response?.data?.message || err?.message || "Session expired. Please login again.";
          return Promise.reject({ message: msg, status: 401, data: err?.response?.data });
        });
      return refreshPromise.then(retryWithNewToken);
    }

    if (status === 401 && config?._isRefreshRequest) {
      refreshPromise = null;
      handleLogout();
      const message = error.response?.data?.message || "Session expired. Please login again.";
      return Promise.reject({ message, status, data: error.response?.data });
    }

    let message;
    switch (status) {
      case 500:
        message = "Internal Server Error";
        break;
      case 404:
        message = "Sorry! the data you are looking for could not be found";
        break;
      default:
        message = error.response?.data?.message || error.message || error;
    }
    return Promise.reject({ message, status, data: error.response?.data });
  }
);
/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  if (token) {
    // Check if token is expired before setting it
    if (isTokenExpired(token)) {
      // Token is expired, logout user
      handleLogout();
      return;
    }
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

class APIClient {
  /**
   * Fetches data from given url
   */

  //  get = (url, params) => {
  //   return axios.get(url, params);
  // };
  get = (url, params) => {
    let response;

    let paramKeys = [];

    if (params) {
      Object.keys(params).map(key => {
        paramKeys.push(key + '=' + params[key]);
        return paramKeys;
      });

      const queryString = paramKeys && paramKeys.length ? paramKeys.join('&') : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };
  /**
   * post given data to url
   */
  create = (url, data) => {
    return axios.post(url, data);
  };
  /**
   * Updates data
   */
  update = (url, data) => {
    return axios.patch(url, data);
  };

  put = (url, data) => {
    return axios.put(url, data);
  };
  /**
   * Delete
   */
  delete = (url, config) => {
    return axios.delete(url, { ...config });
  };
}
const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

export { APIClient, setAuthorization, getLoggedinUser };